import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Lightbox = ({ images, currentIndex, isOpen, onClose, onNavigate }) => {
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowLeft") {
      onNavigate((currentIndex - 1 + images.length) % images.length);
    } else if (e.key === "ArrowRight") {
      onNavigate((currentIndex + 1) % images.length);
    }
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentImage = images[currentIndex];

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-white/70 hover:text-white transition-colors"
        aria-label="Fermer"
        data-testid="lightbox-close"
      >
        <X size={32} />
      </button>

      {/* Previous button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNavigate((currentIndex - 1 + images.length) % images.length);
        }}
        className="absolute left-4 z-10 p-3 text-white/70 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full"
        aria-label="Photo précédente"
        data-testid="lightbox-prev"
      >
        <ChevronLeft size={32} />
      </button>

      {/* Next button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNavigate((currentIndex + 1) % images.length);
        }}
        className="absolute right-4 z-10 p-3 text-white/70 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full"
        aria-label="Photo suivante"
        data-testid="lightbox-next"
      >
        <ChevronRight size={32} />
      </button>

      {/* Image container */}
      <div 
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImage.image}
          alt={currentImage.title}
          className="max-w-full max-h-[75vh] object-contain rounded-lg"
          data-testid="lightbox-image"
        />
        
        {/* Caption */}
        <div className="mt-4 text-center">
          <h3 className="text-white font-heading text-xl font-semibold">
            {currentImage.title}
          </h3>
          <p className="text-white/60 text-sm mt-1">
            {currentImage.category}
          </p>
        </div>

        {/* Dots indicator */}
        <div className="flex gap-2 mt-4">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(index);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? "bg-white w-6" 
                  : "bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`Aller à la photo ${index + 1}`}
              data-testid={`lightbox-dot-${index}`}
            />
          ))}
        </div>

        {/* Counter */}
        <p className="text-white/50 text-sm mt-3">
          {currentIndex + 1} / {images.length}
        </p>
      </div>
    </div>
  );
};

export default Lightbox;
